import { useState, useContext } from 'react'
import CustomTabs from '../../components/common/CustomTabs'
import PersonalInformationsForm from '../../components/forms/personalInformation/personalInformationsForm'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import UpdatePasswordForm from '../../components/forms/personalInformation/personalInformationComponents/updatePasswordForm'
import {
  useGetUser,
  useUpdateUser,
  useUpdateUserPassword,
  useUpdateSession,
} from '../../hooks/api/users'
import { User } from '../../types/api/user'
import { t, use } from 'i18next'
import { Typography } from '@mui/material'
import useSelectedItemSideBar from '../../hooks/useSelectedItemSideBar'
import { ActiveEnterpriseContext } from '../../context/activeEnterpriseContext'
import { PhoneType, OptionalPhoneType } from '../../types'
import {
  createPersonalInformationSchema,
} from '../../validations'
import { extractMainPhoneAndOthers, isMember } from '../../utils'
import useBlocker from '../../hooks/useBlocker'
import TabLoader from '../../components/common/tabLoader'
import Title from '../../components/common/title'
import CustomModal from '../../components/common/modals/customModal'
import BodyModalWithActions from '../../components/common/modals/bodyModalWithActions'
import CustomButton from '../../components/common/buttons/customButton'
import { useImpersonation, clearCookie } from '../../context/impersonationContext'

const initialValues = {
  first_name: '',
  last_name: '',
  email: '',
  recovery_email: '',
  date_of_birth: null as string | null,
  sin_last_four: '',
  mainPhone: { phone_number: '', phone_number_post: '', type: '' as PhoneType },
  otherPhones: [
    { phone_number: '', phone_number_post: '', type: '' as OptionalPhoneType },
    { phone_number: '', phone_number_post: '', type: '' as OptionalPhoneType },
  ],
  otherPhones1: { phone_number: '', phone_number_post: '', type: '' as OptionalPhoneType },
  otherPhones2: { phone_number: '', phone_number_post: '', type: '' as OptionalPhoneType },
  address: {
    city: '',
    city_id: '',
    postal_code: '',
    civic_number: '',
    street: '',
    office: '',
    country: 'Canada',
    province: '',
  },
}
const getInitialValues = (user: User, isMember: boolean) => {
  const { contact, old_email, status, recovery_email, title, titles, ...rest } = user
  return {
    ...rest,
    ...extractMainPhoneAndOthers(contact),
    ...(isMember ? { recovery_email, title, titles: titles ?? [] } : {}),
    address: {
      ...rest.address,
      country: 'Canada',
    },
  }
}

const getValidOptionalPhones = (otherPhones1: any, otherPhones2: any) => {
  const validPhones = []
  
  if (otherPhones1.phone_number && otherPhones1.phone_number.trim() !== '') {
    validPhones.push({
      phone_number: otherPhones1.phone_number,
      phone_number_post: otherPhones1.phone_number_post || '',
      type: otherPhones1.type || '',
    })
  }
  
  if (otherPhones2.phone_number && otherPhones2.phone_number.trim() !== '') {
    validPhones.push({
      phone_number: otherPhones2.phone_number,
      phone_number_post: otherPhones2.phone_number_post || '',
      type: otherPhones2.type || '',
    })
  }
  
  return validPhones
}

const cleanPhoneData = (phone: any) => {
  if (!phone.phone_number || phone.phone_number.trim() === '') {
    return null
  }

  return {
    phone_number: phone.phone_number || '',
    phone_number_post: phone.phone_number_post || '',
    type: phone.type || '',
  }
}

const PersonnalInformations = () => {
  useSelectedItemSideBar('myProfile.personalInformations')
  const { data: user, refetch, isLoading, isFetching: isFetchingUser } = useGetUser()

  const { activeEnterprise } = useContext(ActiveEnterpriseContext)
  const { isImpersonating } = useImpersonation()
  const { mutate: updateSession, error: updateError } = useUpdateSession()
  const [openPopup, setOpenPopup] = useState(false)
  const [openEmailConfirmationPopup, setOpenEmailConfirmationPopup] = useState(false)
  const handleClose = () => {
    setOpenPopup(false)
  }
  const handleCloseEmailConfirmation = () => {
    setOpenEmailConfirmationPopup(false)
  }
  const handleLogout = async () => {
    // Clear impersonation cookie if currently impersonating
    if (isImpersonating) {
      clearCookie('id_token')
    }
    
    await updateSession(null)
    localStorage.clear()
    window.location.href = `${process.env.REACT_APP_ACQ_SITE as string}/logout`
  }
  const formikPersonalInformations = useFormik({
    initialValues: {
      ...(user ? getInitialValues(user, isMember(activeEnterprise)) : initialValues),
    },
    validateOnChange: true,
    enableReinitialize: true,
    validationSchema: createPersonalInformationSchema(isMember(activeEnterprise)),
    onSubmit: async (values) => {
      const {
        mainPhone,
        otherPhones1,
        otherPhones2,
        address: { city, city_id, ...restAddress },
        ...rest
      } = values
      const address = city_id ? { city_id, ...restAddress, country: 'Canada' } : { city, ...restAddress, country: 'Canada' }
      const old_email = values.email !== user?.email ? user?.email : null
      const emailChanged = old_email !== null
      const data = {
        contact: [mainPhone, ...getValidOptionalPhones(otherPhones1, otherPhones2)],
        ...rest,
        address,
        old_email,
      }
      try {
        await mutateAsync(data, {
          onSuccess: async () => {
            await refetch()
            setIsEditable(false)
            if (emailChanged) {
              setOpenEmailConfirmationPopup(true)
            }
          },
        })
      } catch {
        // Already surfaced to the user via useCustomMutationWithFormik's
        // onError (field errors on 422, error modal otherwise) - swallowed
        // here only so formik doesn't rethrow it as an unhandled rejection.
        // mutateAsync (unlike mutate) is required so formik.isSubmitting
        // stays true for the whole request - see EditButtons' disabled prop,
        // which relies on it to block a second click while this is in flight.
      }
    },
  })
  const { mutateAsync } = useUpdateUser(formikPersonalInformations)
  const formikUpdatePassword = useFormik({
    initialValues: {
      newPassword: '',
      confirmPassword: '',
    },
    validationSchema: Yup.object({
      newPassword: Yup.string()
        .matches(
          /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^A-Za-z0-9]).{12,}$/,
          `Mot de passe invalide. Votre mot de passe doit comporter au moins 12 caractères et 
          être composé d'au moins un chiffre, une majuscule, une minuscule et un caractère spécial.`,
        )
        .required('Obligatoire'),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('newPassword'), null], 'Les mots de passe doivent correspondre')
        .required('Obligatoire'),
    }),
    onSubmit: async (values, formikBag) => {
      const { newPassword: password, confirmPassword: password_confirmation } = values

      const data = {
        password,
        password_confirmation,
      }
      await mutateUpdateUserPassword(data, {
        onSuccess: async () => {
          formikBag.resetForm()
          setOpenPopup(true)
          // handleLogout()
        },
      })
    },
  })
  const { mutate: mutateUpdateUserPassword, isLoading: isLoadingUpdatePassword } =
    useUpdateUserPassword(formikUpdatePassword)
  useBlocker(formikPersonalInformations.dirty || formikUpdatePassword.dirty)
  const [showPassword, setShowPassword] = useState({
    newPassword: false,
    confirmPassword: false,
  })

  const handleShowPassword = (key: 'newPassword' | 'confirmPassword') =>
    setShowPassword((showPassword) => ({ ...showPassword, [key]: !showPassword[key] }))
  const [isEditable, setIsEditable] = useState(false)
  return (
    <>
      <Title>{t('title')}</Title>
      <CustomTabs
        list={[
          {
            label: 'Coordonnées',
            element: (
              <TabLoader isLoading={isLoading || isFetchingUser}>
                <PersonalInformationsForm
                  formik={formikPersonalInformations}
                  isEditable={isEditable}
                  setIsEditable={setIsEditable}
                />
              </TabLoader>
            ),
            width: '176px',
          },
          {
            label: 'Mot de passe',
            element: (
              <TabLoader isLoading={isLoadingUpdatePassword}>
                <UpdatePasswordForm
                  handleShowPassword={handleShowPassword}
                  showPassword={showPassword}
                  formik={formikUpdatePassword}
                />
              </TabLoader>
            ),
            width: '199px',
          },
        ]}
      />

      <CustomModal
        open={openPopup}
        handleClose={handleClose}
        sx={{ 
          width: {
            xs: '90vw',
            sm: '80vw',
            md: '700px',
          },
          '& .closeIcon': { display: 'none' } 
        }}
        backdropClick={true}
      >
        <BodyModalWithActions
          title='Votre mot de passe a été mis à jour'
          type='confirm'
          content={
            <Typography variant='body1'>
              Veuillez-vous reconnecter avec votre nouveau mot de passe.
            </Typography>
          }
          sx={{
            alignItems: 'center',
            '& .modal-header': {
              flexDirection: 'column',
            },
          }}
          actions={
            <>
              <CustomButton variant='secondary' onClick={handleLogout}>
                Me connecter
              </CustomButton>
            </>
          }
        />
      </CustomModal>

      <CustomModal
        open={openEmailConfirmationPopup}
        handleClose={handleCloseEmailConfirmation}
        sx={{ 
          width: {
            xs: '90vw',
            sm: '80vw',
            md: '600px',
          },
        }}
        backdropClick={true}
      >
        <BodyModalWithActions
          title='Confirmation de changement de courriel'
          type='confirm'
          content={
            <Typography variant='body1'>
              Un courriel de confirmation a été envoyé à votre nouvelle adresse. 
              Veuillez vérifier votre boîte de réception pour confirmer le changement.
            </Typography>
          }
          sx={{
            alignItems: 'center',
            '& .modal-header': {
              flexDirection: 'column',
            },
          }}
          actions={
            <>
              <CustomButton variant='secondary' onClick={handleCloseEmailConfirmation}>
                J'ai compris
              </CustomButton>
            </>
          }
        />
      </CustomModal>
    </>
  )
}

export default PersonnalInformations